import { useCallback, useEffect, useState } from "react";
import { currentCustodySession, submitWithdrawal } from "../wallet/custodial/api";
import { useWallet } from "../wallet/WalletProvider";
import { useGen1Nav } from "./useGen1Nav";

const ASSETS = ["rSOL", "RCD"] as const;

/**
 * Builds a withdraw approval to the connected wallet and asks that wallet to sign it (§9.5).
 */
export function WithdrawScreen({ onBack, onDone }: { onBack: () => void; onDone: (text: string) => void }) {
  const { wallet } = useWallet();
  const [assetIndex, setAssetIndex] = useState(0);
  const [amount, setAmount] = useState("");
  const [destination, setDestination] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancel = false;
    void Promise.resolve(currentCustodySession()?.connectedWallet ?? null).then((connected) => {
      if (!cancel) setDestination(connected);
    })
      .catch((e: unknown) => {
        if (!cancel) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancel = true;
    };
  }, []);

  const submit = useCallback(async () => {
    if (!wallet || busy) return;
    const asset = assetIndex === 0 ? "rsol" : "rcd";
    const trimmed = amount.trim();
    if (!trimmed || !destination) {
      setError(destination ? "Enter an amount." : "Connected wallet isn't available yet.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const connected = destination;
      const { getWallets } = await import("@wallet-standard/app");
      const standard = getWallets()
        .get()
        .find((w) => w.accounts.some((a) => a.address === connected));
      const account = standard?.accounts.find((a) => a.address === connected);
      if (!standard || !account) throw new Error("Reconnect the wallet this withdrawal pays.");
      const res = await submitWithdrawal({ wallet: standard, account, asset, amount: trimmed, destination: connected });
      onDone(res.signature ? "Withdrawal sent." : "Withdrawal requested.");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [wallet, busy, assetIndex, amount, destination, onDone]);

  const items = ["ASSET", "CONFIRM", "BACK"];
  const onA = useCallback(
    (i: number) => {
      if (items[i] === "ASSET") setAssetIndex((n) => (n + 1) % ASSETS.length);
      else if (items[i] === "CONFIRM") void submit();
      else onBack();
    },
    [items, submit, onBack],
  );
  const { index } = useGen1Nav(busy ? 0 : items.length, onA, onBack);

  return (
    <>
      <h2 className="wallet-menu__title">WITHDRAW</h2>
      <p className="wallet-menu__hint">To your connected wallet. You'll approve the exact text in that wallet.</p>
      <p className="wallet-menu__hint">
        {ASSETS[assetIndex]}{" "}
        <input
          autoFocus
          inputMode={assetIndex === 0 ? "decimal" : "numeric"}
          value={amount}
          onChange={(e) => setAmount(assetIndex === 0 ? e.target.value.replace(/[^\d.]/g, "") : e.target.value.replace(/\D/g, ""))}
        />
      </p>
      {destination && <p className="wallet-menu__address">{destination}</p>}
      {error && <p className="wallet-menu__status">{error}</p>}
      {busy && <p className="wallet-menu__status">Waiting for approval…</p>}
      <ul className="wallet-menu__list" role="listbox">
        {items.map((label, i) => (
          <li key={label} role="option" aria-selected={i === index} onClick={() => onA(i)}>
            <span className="wallet-menu__cursor" aria-hidden="true">
              {i === index ? "\u25b6" : "\u00a0"}
            </span>
            <span className="wallet-menu__label">{label === "ASSET" ? ASSETS[assetIndex] : label}</span>
          </li>
        ))}
      </ul>
    </>
  );
}
